import { type ReactElement } from 'react';
import { useFetcher } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Alert, Button } from '@marketplace/ui';
import { type Job } from '@marketplace/contracts';

/** What the job page's redeem answers: `null` on success, or the refusal as a key. */
export interface RedeemActionData {
  redeemError: string | null;
}

/**
 * `W4-T09` §4.4 — the client's side of the handshake on their own job page.
 *
 * On an `AWARDED` job: the hint to scan the professional's code, and the six-digit form for a client
 * whose camera fails or who is on a laptop (E6). Typing the code and pressing Confirm is the
 * deliberate act (§2.1); the job page's action redeems it with this job's id, so a code is only ever
 * compared against this booking's live one. On an `IN_PROGRESS` job: when the work started.
 */
export function StartPanel({
  job,
  professional,
  locale,
}: {
  job: Job;
  /** The awarded professional's name, when the page knows it (an auction's award does not, yet). */
  professional: string | null;
  locale: string;
}): ReactElement | null {
  const { t } = useTranslation();
  const redeem = useFetcher<RedeemActionData>();

  if (job.status === 'IN_PROGRESS') {
    return job.startedAt === null ? null : (
      <p className="mp-lead" data-testid="handshake-started">
        {t('handshake.client.started', {
          date: new Date(job.startedAt).toLocaleString(locale, {
            dateStyle: 'medium',
            timeStyle: 'short',
          }),
        })}
      </p>
    );
  }
  if (job.status !== 'AWARDED') return null;

  const error = redeem.data?.redeemError ?? null;

  return (
    <section
      className="mp-section"
      aria-labelledby="handshake-client-title"
      data-testid="handshake-client"
    >
      <h2 id="handshake-client-title">{t('handshake.start.title')}</h2>
      <p className="mp-lead">{t('handshake.client.scanHint')}</p>
      <redeem.Form method="post" className="mp-form">
        <input type="hidden" name="intent" value="redeem" />
        <label htmlFor="handshake-short-code">{t('handshake.client.codeLabel')}</label>
        <input
          id="handshake-short-code"
          name="shortCode"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          aria-invalid={error === null ? undefined : true}
        />
        <p>
          {professional === null
            ? t('handshake.client.confirmAnyone')
            : t('handshake.client.confirm', { name: professional })}
        </p>
        <Button type="submit" variant="primary" isDisabled={redeem.state !== 'idle'}>
          {t('handshake.client.confirmButton')}
        </Button>
        {error === null ? null : <Alert tone="error">{t(error)}</Alert>}
      </redeem.Form>
    </section>
  );
}
